
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Route } from 'vue-router';
import api from '@/api';
import Util from '@/utils/util';
import Swal from 'sweetalert2';
import DateRange from '@/components/DateRange.vue';
import adBlockType from '@/enums/adBlockType';

const { appAd } = api;

@Component({
  components: { DateRange },
})
export default class AppAdvertisementList extends Vue {
  private data = { data: [], totalCount: 0, pageSize: 8, pageNo: 0 };

  private loading = false;

  private panel = 0;

  private blockType: adBlockType = adBlockType.None;

  private headers = [
    { text: '廣告標題', value: 'title' },
    { text: '上架時間', value: 'startTime' },
    { text: '下架時間', value: 'endTime' },
    { text: '排序', value: 'priority' },
    { text: '啟用狀態', value: 'status' },
    { text: '修改', value: 'edit' },
    { text: '刪除', value: 'delete' },
  ];

  private get totalCount() {
    return this.data.totalCount;
  }

  private get pageCount() {
    return Math.ceil((this.data.totalCount || 0) / (this.data.pageSize || 20));
  }

  private get collection() {
    return [...(this.data.data || [])];
  }

  private get createPath() {
    return this.blockType === adBlockType.Popup ? 'appAdPopupCreate' : 'appAdCarouselCreate';
  }

  private get editPath() {
    return this.blockType === adBlockType.Popup ? 'appAdPopupEdit' : 'appAdCarouselEdit';
  }

  private filter = {
    q: this.$route.query.q ? this.$route.query.q : '',
    startStartOn: this.$route.query.startStartOn || undefined,
    endStartOn: this.$route.query.endStartOn || undefined,
    startEndOn: this.$route.query.startEndOn || undefined,
    endEndOn: this.$route.query.endEndOn || undefined,
    pageSize: Util.isNullOrUndefined(this.$route.query.pageSize)
      ? 20
      : +this.$route.query.pageSize,
    pageNo: Util.isNullOrUndefined(this.$route.query.pageNo)
      ? 1
      : +this.$route.query.pageNo,
  };

  private async handleEnableStatus(adId: any, IsEnabled: boolean) {
    const message = IsEnabled ? '停用' : '啟用';
    await Swal.fire(
      {
        title: `您確定要${message}嗎?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
      },
    ).then(async (result) => {
      if (result.isConfirmed) {
        const { status } = IsEnabled ? await appAd.disable(adId, this.blockType) : await appAd.enable(adId, this.blockType);
        if (status === 1) {
          await Swal.fire({ icon: 'success', title: `${message}成功` });
          this.requestData();
        } else {
          await Swal.fire({ icon: 'warning', title: `${message}失敗，請聯繫系統管理員。` });
        }
      }
    });
  }

  private async handleOpenDelete(adId: any) {
    await Swal.fire(
      {
        title: '您確定要刪除嗎?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
      },
    ).then(async (result) => {
      if (result.isConfirmed) {
        const { status } = await appAd.delete(adId, this.blockType);
        if (status === 1) {
          await Swal.fire({ icon: 'success', title: '刪除成功' });
          this.requestData();
        } else {
          await Swal.fire({ icon: 'warning', title: '刪除失敗，請聯繫系統管理員。' });
        }
      }
    });
  }

  private async updatePage(page: number) {
    this.filter.pageNo = page;
    await this.query();
  }

  private async search() {
    this.filter.pageNo = 1;
    await this.query();
  }

  private async query() {
    const query = {
      ...this.filter,
      pageSize: `${this.filter.pageSize}`,
      pageNo: `${this.filter.pageNo}`,
    };
    if (this.isQueryChanged(query)) {
      await this.$router.replace({ query });
    }
    await this.requestData();
  }

  private async reset() {
    this.filter = {
      q: '',
      startStartOn: undefined,
      endStartOn: undefined,
      startEndOn: undefined,
      endEndOn: undefined,
      pageSize: 20,
      pageNo: 1,
    };
    await this.query();
  }

  private isQueryChanged(query: {
    [key: string]: string | Array<string | null> | null | undefined;
  }): boolean {
    return !Object.keys(query).every(
      (key: string) => query[key]?.toString() === this.$route.query[key],
    );
  }

  private async requestData() {
    this.loading = true;
    const { data } = await appAd.list(this.filter, this.blockType);
    this.data = data;
    this.loading = false;
  }

  private setBlockType(blockType: adBlockType) {
    this.blockType = blockType;
  }

  private async beforeRouteEnter(
    to: Route,
    from: Route,
    next: (to?: ((vm: AppAdvertisementList) => void) | boolean) => void,
  ) {
    await next(async (vm) => {
      const blockType = to.name === 'appAdPopupList' ? adBlockType.Popup : adBlockType.Carousel;
      vm.setBlockType(blockType);
      await vm.requestData();
    });
  }
}
